import React from 'react';
import { useSite } from '../context/SiteContext';
import { useLang } from '../context/LangContext';
import { pickI18n } from '../i18n/helpers';
import { Film, Mail, MapPin, Phone } from 'lucide-react';
import Logo from './Logo';
import LangSwitcher from './LangSwitcher';
import { Link } from 'react-router-dom';

const Footer = () => {
  const { content } = useSite();
  const { t, lang } = useLang();
  const contactInfo = content?.contact || {};

  const links = [
    { label: t('nav.home'), href: '#inicio' },
    { label: t('nav.about'), href: '#nosotros' },
    { label: t('nav.services'), href: '#servicios' },
    { label: t('nav.pricing'), href: '#tarifas' },
    { label: t('nav.works'), href: '#trabajos' },
    { label: t('nav.contact'), href: '#contacto' },
  ];

  return (
    <footer className="relative bg-[#070f18] border-t border-white/5 pt-16 sm:pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-12 gap-10 sm:gap-12 pb-12 sm:pb-14 border-b border-white/5">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-5">
            <Logo size="md" className="mb-6" />
            <p className="text-white/55 text-[14px] leading-relaxed max-w-md">
              {t('footer.description')}
            </p>

            <div className="mt-7 flex flex-wrap gap-2">
              {['DCI', 'SMPTE', 'AES-128', 'ISO 27001'].map((tag) => (
                <span
                  key={tag}
                  className="text-[10px] px-2.5 py-1 rounded-md border border-white/10 text-white/50 uppercase tracking-widest"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-7">
              <LangSwitcher />
            </div>
          </div>

          {/* Nav */}
          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#00e0c7] mb-5">
              {t('footer.navigation')}
            </div>
            <ul className="space-y-3">
              {links.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    className="text-white/65 hover:text-white text-[14px] transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#00e0c7] mb-5">
              {t('footer.contact')}
            </div>
            <ul className="space-y-3 text-[14px] text-white/65">
              {contactInfo.email && (
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-[#00e0c7] mt-0.5 flex-shrink-0" />
                  <span className="break-all">{contactInfo.email}</span>
                </li>
              )}
              {contactInfo.phone && (
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-[#00e0c7] mt-0.5 flex-shrink-0" />
                  {contactInfo.phone}
                </li>
              )}
              {contactInfo.address && (
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#00e0c7] mt-0.5 flex-shrink-0" />
                  {pickI18n(contactInfo.address, lang)}
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-[12px] text-white/35">
            © {new Date().getFullYear()} {content?.branding?.companyName || 'Laboratorio Digital de Cine'}. {t('footer.rights')}
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-white/35">
            <Link to="/legal/aviso-legal" className="hover:text-white/80 transition-colors">
              {t('footer.legal')}
            </Link>
            <Link to="/legal/privacidad" className="hover:text-white/80 transition-colors">
              {t('footer.privacy')}
            </Link>
            <Link to="/legal/cookies" className="hover:text-white/80 transition-colors">
              {t('footer.cookies')}
            </Link>
            <span className="flex items-center gap-1.5">
              <Film className="w-3.5 h-3.5" />
              v1.0
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
